import {
  useEffect, useRef, useState, useCallback,
} from 'react';
import { postHeartbeat } from '../requests/api/sessions';
import AuthService from '../services/authService';

const HEARTBEAT_INTERVAL = 120000;

const useSessionTracking = (sessionId) => {
  const [isActive, setIsActive] = useState(true);
  const [currentSessionId, setCurrentSessionId] = useState(sessionId);
  const heartbeatIntervalRef = useRef(null);
  const sessionIdRef = useRef(sessionId);

  useEffect(() => {
    sessionIdRef.current = sessionId;
    setCurrentSessionId(sessionId);
  }, [sessionId]);

  const sendHeartbeat = useCallback(async () => {
    const activeSessionId = sessionIdRef.current;
    if (!activeSessionId || !isActive) {
      return;
    }

    try {
      const response = await postHeartbeat(activeSessionId);
      if (response.success
        && response.data?.sessionId
        && response.data.sessionId !== activeSessionId) {
        AuthService.setSessionId(response.data.sessionId);
        sessionIdRef.current = response.data.sessionId;
        setCurrentSessionId(response.data.sessionId);
      }
    } catch {
      console.warn('Error sending heartbeat');
    }
  }, [isActive]);

  useEffect(() => {
    if (!currentSessionId) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsActive(isVisible);

      if (isVisible) {
        sendHeartbeat();
      }
    };

    const handleFocus = () => {
      setIsActive(true);
      sendHeartbeat();
    };

    const handleBlur = () => {
      setIsActive(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    if (isActive) {
      sendHeartbeat();
      heartbeatIntervalRef.current = setInterval(() => {
        if (isActive && !document.hidden) {
          sendHeartbeat();
        }
      }, HEARTBEAT_INTERVAL);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);

      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [currentSessionId, isActive, sendHeartbeat]);
};

export default useSessionTracking;
